/**
 * Componente de vista para verificación de eventos por documento.
 *
 * Muestra una interfaz optimizada para móviles que permite buscar eventos
 * asociados a un documento de identidad y visualizar los resultados en una
 * tabla responsiva con información relevante.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date 2025-10-22
 * @updated 2025-10-22
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Divider,
  Typography,
  IconButton,
  Collapse,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import MDAlert from "components/Alert";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import MDBadge from "components/MDBadge";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import QRScanner from "components/QRScanner/QRScanner";
import "./verifyEventsByDocument.css";

/**
 * Componente principal para la verificación de eventos por documento.
 *
 * @param {Function} handleFieldChange - Función para manejar cambios en los campos del formulario.
 * @param {Function} handleSubmit - Función para ejecutar la búsqueda.
 * @param {Array} data - Array de eventos encontrados.
 * @param {boolean} isLoading - Indica si se está cargando la información.
 * @returns {JSX.Element} Componente de React.
 */
export default function VerifyEventsByDocument({
  handleFieldChange,
  handleSubmit,
  data,
  isLoading,
}) {
  const [hasSearched, setHasSearched] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAllColumns, setShowAllColumns] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!isMobile) {
      setShowAllColumns(true);
    }
  }, [isMobile]);

  /**
   * Maneja el envío del formulario de búsqueda.
   */
  const handleSearchSubmit = () => {
    setHasSearched(true);
    handleSubmit();
  };

  /**
   * Maneja la visualización de alertas.
   *
   * @param {string} type - Tipo de alerta ("success" o "fail").
   * @param {string} message - Mensaje a mostrar.
   */
  const handleAlert = (type, message) => {
    setTypeAlert(type);
    setAlertMessage(message || "");
    setIsAlert(true);
  };

  /**
   * Maneja la detección de códigos QR escaneados.
   * Navega automáticamente si el QR contiene una ruta válida de verificación.
   *
   * @param {string} scannedRaw - Texto escaneado del código QR.
   */
  const handleQRDetected = (scannedRaw) => {
    const scanned = (scannedRaw || "").trim();

    const verifyMatch = scanned.match(/(\/verify\/[^\/]+\/(?:true|false)\/?)/i);
    if (verifyMatch && verifyMatch[1]) {
      navigate(verifyMatch[1]);
      return;
    }

    if (/^https?:\/\//i.test(scanned)) {
      try {
        const parsed = new URL(scanned);
        if (parsed.pathname && /\/verify\//i.test(parsed.pathname)) {
          const path = parsed.pathname + parsed.search + parsed.hash;
          navigate(path);
          return;
        }
        handleAlert("fail", "QR no es válido");
        return;
      } catch (e) {
        handleAlert("fail", "QR no es válido");
        return;
      }
    }

    if (scanned.startsWith("/")) {
      if (/\/verify\//i.test(scanned)) {
        navigate(scanned);
        return;
      }
      handleAlert("fail", "QR no es válido");
      return;
    }

    const looksLikeId = /^[0-9A-Za-z\-_.@]{3,}$/.test(scanned);
    if (looksLikeId) {
      handleFieldChange("name", scanned);
      setHasSearched(true);
      if (typeof handleSubmit === "function") handleSubmit();
      return;
    }

    handleAlert("fail", "QR no es válido");
  };

  /**
   * Formatea una fecha a formato local colombiano.
   *
   * @param {string} dateString - Fecha en formato ISO.
   * @returns {string} Fecha formateada.
   */
  const getLocalDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return date.toLocaleString("es-CO", options);
  };

  return (
    <>
      <Typography variant="h3" component="h1" gutterBottom>
        Verificación de eventos por documento
      </Typography>
      <MDAlert
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        typeAlert={typeAlert}
        message={alertMessage}
      />
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        Ingresa el número de documento:
      </Typography>

      <div className="input-with-scanner">
        <MDInput
          style={{ marginTop: "5px" }}
          label=""
          onChange={(e) => handleFieldChange("name", e.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSearchSubmit();
            }
          }}
        />

        <div className="qr-scanner-position">
          <QRScanner
            onDetected={handleQRDetected}
            iconOnly={true}
            tooltip="Escanear QR"
            icon={
              <div aria-hidden="true" className="qr-icon">
                <CameraAltIcon style={{ color: "#1976d2" }} />
              </div>
            }
          />
        </div>
      </div>

      <Divider variant="middle" />

      <MDButton onClick={handleSearchSubmit} type="submit" color="success">
        Consultar
      </MDButton>

      {isMobile && data && data.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <MDButton
            onClick={() => setShowAllColumns(!showAllColumns)}
            color="info"
            variant="outlined"
            size="small"
            endIcon={showAllColumns ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showAllColumns ? "Ocultar columnas" : "Mostrar todas las columnas"}
          </MDButton>
        </Box>
      )}

      <div style={{ width: "auto", margin: "2rem" }}>
        {isLoading ? (
          <div>Cargando...</div>
        ) : data && data.length > 0 ? (
          <DataTable
            table={{
              columns:
                isMobile && !showAllColumns
                  ? [
                      { Header: "Evento", accessor: "event_name", width: "70%" },
                      { Header: "Acciones", accessor: "view", width: "30%", disableSortBy: true },
                    ]
                  : [
                      { Header: "Evento", accessor: "event_name", width: "30%" },
                      { Header: "Estado", accessor: "status", width: "15%" },
                      { Header: "Válido hasta", accessor: "expires_in", width: "20%" },
                      { Header: "Ingreso", accessor: "joined", width: "15%" },
                      { Header: "Acciones", accessor: "view", width: "20%", disableSortBy: true },
                    ],
              rows: data?.map((item) => ({
                event_name: item.data_source_name || "Sin nombre",
                status:
                  item.status === "Activo" ? (
                    <MDBadge badgeContent="Activo" size="xs" container color="success" />
                  ) : (
                    <MDBadge badgeContent={item.status} size="xs" container color="error" />
                  ),
                expires_in: getLocalDate(item.expires_in),
                joined:
                  item.Tipo_carnet === "Invitado" || item.Tipo_carnet === "Visitante" ? (
                    item.joined == 1 ? (
                      <CheckCircleIcon style={{ color: "green" }} />
                    ) : (
                      <QueryBuilderIcon style={{ color: "red" }} />
                    )
                  ) : (
                    "N/A"
                  ),
                view: (
                  <a style={{ cursor: "pointer" }} href={`/verify/${item.id}/false`}>
                    <FindInPageOutlinedIcon
                      style={{
                        fontSize: "24px",
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </a>
                ),
              })),
            }}
            canSearch={false}
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={false}
            checkboxSelection={false}
          />
        ) : (
          <div>
            {hasSearched ? "No hay resultados" : "Realiza una búsqueda para ver los resultados"}
          </div>
        )}
      </div>
    </>
  );
}
